import React, { useState, useEffect, useRef, useCallback } from "react";
import QrScanner from "qr-scanner";
import {
  Flashlight,
  FlashlightOff,
  Copy,
  Check,
  Upload,
  Camera as CameraIcon,
  X,
  ArrowUp,
  ShieldAlert,
  RefreshCw,
  Scan,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  checkCameraPermission,
  requestCameraStream,
  getCameraErrorMessage,
  isSecureCameraContext,
} from "../utils/cameraPermissions";

const BarcodeScanner = ({ onScanResult, onError, className = "" }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [permissionState, setPermissionState] = useState("loading"); // 'loading' | 'prompt' | 'granted' | 'denied' | 'unsupported'
  const [permissionError, setPermissionError] = useState(null);
  const [isCameraLoading, setIsCameraLoading] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [isResultCopied, setIsResultCopied] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  // Detected code outline in video pixel coordinates: { points: [{x,y}], w, h }
  const [codeBox, setCodeBox] = useState(null);
  const [isDark, setIsDark] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanningIntervalRef = useRef(null);
  const busyRef = useRef(false);
  const frameCountRef = useRef(0);
  const darkRef = useRef(false);
  const qrEngineRef = useRef(null);
  const fileInputRef = useRef(null);

  // Stop camera stream safely
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (e) {
          console.warn("Error stopping track:", e);
        }
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (scanningIntervalRef.current) {
      clearInterval(scanningIntervalRef.current);
      scanningIntervalRef.current = null;
    }
    setIsScanning(false);
    setIsCameraLoading(false);
    setIsCameraReady(false);
    setFlashEnabled(false);
    setCodeBox(null);
    setIsDark(false);
    darkRef.current = false;
  }, []);

  // Enumerate video input devices
  const getVideoDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        return;
      }
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = allDevices.filter(
        (device) => device.kind === "videoinput"
      );
      setDevices(videoDevices);
      if (videoDevices.length > 0 && !selectedDevice) {
        setSelectedDevice(videoDevices[0].deviceId);
      }
    } catch (error) {
      console.warn("Error enumerating video devices:", error);
    }
  }, [selectedDevice]);

  // Start camera stream with permission handling and fallback constraints
  const startCamera = useCallback(
    async (deviceIdOverride = null) => {
      setIsCameraLoading(true);
      setIsCameraReady(false);
      setPermissionError(null);

      try {
        // Stop any existing stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        const targetDeviceId = deviceIdOverride !== null ? deviceIdOverride : selectedDevice;
        const videoConstraints = targetDeviceId
          ? { deviceId: { exact: targetDeviceId } }
          : {};

        const stream = await requestCameraStream(videoConstraints);
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // Ensure attributes are explicitly set for mobile and iOS Safari
          videoRef.current.setAttribute("playsinline", "true");
          videoRef.current.setAttribute("autoplay", "true");
          videoRef.current.muted = true;

          videoRef.current.onloadedmetadata = () => {
            if (videoRef.current) {
              const playPromise = videoRef.current.play();
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    setIsCameraReady(true);
                    setIsCameraLoading(false);
                  })
                  .catch((err) => {
                    console.warn("Video play promise error:", err);
                    setIsCameraReady(true);
                    setIsCameraLoading(false);
                  });
              } else {
                setIsCameraReady(true);
                setIsCameraLoading(false);
              }
            }
          };

          // Also trigger play immediately
          const initialPlay = videoRef.current.play();
          if (initialPlay !== undefined) {
            initialPlay.catch(() => {});
          }
        }

        setPermissionState("granted");
        setIsScanning(true);
        await getVideoDevices();
      } catch (error) {
        console.error("Error accessing camera:", error);
        stopCamera();

        const errMsg = getCameraErrorMessage(error);
        setPermissionError(errMsg);

        if (
          error.name === "NotAllowedError" ||
          error.name === "PermissionDeniedError" ||
          (error.message && error.message.includes("Permission denied"))
        ) {
          setPermissionState("denied");
        } else if (!isSecureCameraContext()) {
          setPermissionState("unsupported");
        } else {
          setPermissionState("denied");
        }

        if (onError) {
          onError(errMsg);
        }
      } finally {
        setIsCameraLoading(false);
      }
    },
    [selectedDevice, onError, getVideoDevices, stopCamera]
  );

  // Check initial permission state and auto-start camera
  useEffect(() => {
    let isMounted = true;

    const initScanner = async () => {
      const state = await checkCameraPermission();
      if (!isMounted) return;

      setPermissionState(state);

      if (state === "granted") {
        await startCamera();
      } else if (state === "unsupported") {
        setPermissionError(
          "Perangkat atau browser tidak mendukung akses kamera atau memerlukan HTTPS."
        );
      } else {
        // Auto-attempt start to trigger native browser prompt if in 'prompt' state
        await startCamera();
      }
    };

    initScanner();

    return () => {
      isMounted = false;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run on mount

  // One shared qr-scanner engine (native BarcodeDetector or its web worker) for all scans.
  useEffect(() => {
    qrEngineRef.current = QrScanner.createQrEngine();
    Promise.resolve(qrEngineRef.current).then((e) =>
      console.log("[QR debug] engine:", typeof Worker !== "undefined" && e instanceof Worker ? "web worker (jsQR)" : "native BarcodeDetector")
    );
    return () => {
      const engine = qrEngineRef.current;
      qrEngineRef.current = null;
      Promise.resolve(engine)
        .then((e) => e && e.terminate && e.terminate())
        .catch(() => {});
    };
  }, []);

  // Resolves to { data, cornerPoints } or null when no QR code is found.
  const decodeQr = useCallback(async (source, scanRegion) => {
    const t0 = performance.now();
    try {
      const res = await QrScanner.scanImage(source, {
        scanRegion,
        returnDetailedScanResult: true,
        qrEngine: await qrEngineRef.current,
      });
      console.log("[QR debug] decoded", { data: res.data, corners: res.cornerPoints, ms: Math.round(performance.now() - t0) });
      return res;
    } catch (e) {
      console.log("[QR debug] no QR", { reason: String(e), ms: Math.round(performance.now() - t0) });
      return null;
    }
  }, []);

  // Toggle flash/torch
  const toggleFlash = useCallback(async () => {
    if (!streamRef.current) return;

    try {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack && videoTrack.getCapabilities) {
        const capabilities = videoTrack.getCapabilities();
        if (capabilities.torch) {
          await videoTrack.applyConstraints({
            advanced: [{ torch: !flashEnabled }],
          });
          setFlashEnabled(!flashEnabled);
        } else {
          toast.error("Flash tidak didukung pada perangkat ini");
        }
      } else {
        toast.error("Flash tidak didukung pada perangkat ini");
      }
    } catch (error) {
      console.error("Error toggling flash:", error);
      toast.error("Gagal mengubah status flash");
    }
  }, [flashEnabled]);

  // Copy result to clipboard
  const copyResult = useCallback(async () => {
    if (!lastResult) return;

    try {
      await navigator.clipboard.writeText(lastResult);
      setIsResultCopied(true);
      toast.success("Hasil berhasil disalin ke clipboard!");
      setTimeout(() => setIsResultCopied(false), 2000);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast.error("Gagal menyalin ke clipboard");
    }
  }, [lastResult]);

  // Validate barcode/QR code result
  const validateScanResult = useCallback((result) => {
    if (!result || typeof result !== "string") {
      return false;
    }

    // Product filtering (MMS / Calcium / unrecognized) happens in BarcodeScannerScreen.
    return result.trim().length >= 3;
  }, []);

  // Process uploaded image for QR codes
  const processUploadedImage = useCallback(
    async (file) => {
      // Full-res gallery photos are too big for jsQR (slow, hits the 10s timeout); scan a downscaled copy first.
      let code = null;
      try {
        const bmp = await createImageBitmap(file);
        const scale = Math.min(1, 1000 / Math.max(bmp.width, bmp.height));
        code = await decodeQr(bmp, {
          x: 0,
          y: 0,
          width: bmp.width,
          height: bmp.height,
          downScaledWidth: Math.round(bmp.width * scale),
          downScaledHeight: Math.round(bmp.height * scale),
        });
      } catch (e) {
        console.warn("Downscaled QR scan failed:", e);
      }
      if (!code) code = await decodeQr(file);
      if (code && code.data) {
        const result = code.data;
        if (validateScanResult(result)) {
          if (typeof navigator !== "undefined" && navigator.vibrate) {
            try {
              navigator.vibrate(200);
            } catch (e) {
              console.warn("Vibration feedback error:", e);
            }
          }

          setLastResult(result);
          if (onScanResult) {
            onScanResult(result);
          }
          toast.success("QR / Barcode terdeteksi dari gambar!");
        } else {
          toast.error("Barcode tidak valid pada gambar yang diunggah");
        }
      } else {
        toast.error("Tidak ditemukan QR code / barcode pada gambar ini");
      }
    },
    [onScanResult, validateScanResult, decodeQr]
  );

  // Handle file upload
  const handleFileUpload = useCallback(
    (file) => {
      if (!file) return;

      const validTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (file.type && !validTypes.includes(file.type)) {
        toast.error("Silakan unggah file gambar yang valid (JPEG, PNG, GIF, WebP)");
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error("Ukuran file terlalu besar. Maksimal 10MB");
        return;
      }

      setUploadedImage(file);
      processUploadedImage(file);
    },
    [processUploadedImage]
  );

  // Handle drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload]
  );

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) {
        handleFileUpload(file);
      }
    },
    [handleFileUpload]
  );

  // Clear uploaded image
  const clearUploadedImage = useCallback(() => {
    setUploadedImage(null);
    setLastResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleResult = useCallback(
    (result) => {
      if (!validateScanResult(result) || result === lastResult) {
        console.log("[QR debug] result ignored", { result, valid: validateScanResult(result), duplicate: result === lastResult });
        return;
      }
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        try {
          navigator.vibrate(200);
        } catch (e) {
          console.warn("Vibration feedback error:", e);
        }
      }
      setLastResult(result);
      setIsScanning(false);
      stopCamera();
      if (onScanResult) {
        onScanResult(result);
      }
      toast.success("Barcode berhasil dipindai!");
    },
    [lastResult, onScanResult, validateScanResult, stopCamera]
  );

  // Scan frame continuously with qr-scanner (it uses native BarcodeDetector when available).
  const scanFrame = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !isScanning) return;
    if (busyRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return;

    if (
      video.readyState >= (video.HAVE_CURRENT_DATA || 2) &&
      video.videoWidth > 0 &&
      video.videoHeight > 0
    ) {
      busyRef.current = true;
      try {
        const vw = video.videoWidth;
        const vh = video.videoHeight;

        // Every ~10 frames, sample mean luminance on a tiny copy to detect low light.
        if (frameCountRef.current++ % 10 === 0) {
          canvas.width = 32;
          canvas.height = 24;
          context.drawImage(video, 0, 0, 32, 24);
          const px = context.getImageData(0, 0, 32, 24).data;
          let sum = 0;
          for (let i = 0; i < px.length; i += 4) {
            sum += 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
          }
          const dark = sum / (px.length / 4) < 60;
          if (dark !== darkRef.current) {
            darkRef.current = dark;
            setIsDark(dark);
          }
        }

        // Alternate full frame and a 2x-upscaled center crop so small QR codes get enough pixels per module.
        const crop = frameCountRef.current % 2 === 0;
        const sw = crop ? Math.round(vw / 2) : vw;
        const sh = crop ? Math.round(vh / 2) : vh;
        const sx = (vw - sw) / 2;
        const sy = (vh - sh) / 2;
        const scale = crop ? 2 : 1;
        canvas.width = sw * scale;
        canvas.height = sh * scale;
        // Boost contrast/brightness in low light so the decoder can binarize the frame.
        context.filter = darkRef.current ? "contrast(1.8) brightness(1.5)" : "none";
        context.imageSmoothingEnabled = false; // keep module edges sharp when upscaling
        context.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
        console.log("[QR debug] frame", { w: vw, h: vh, crop, dark: darkRef.current });
        const code = await decodeQr(canvas);

        if (code && code.data) {
          if (code.cornerPoints && code.cornerPoints.length >= 4) {
            const points = code.cornerPoints.map((p) => ({ x: sx + p.x / scale, y: sy + p.y / scale }));
            setCodeBox({ points, w: vw, h: vh });
          }
          handleResult(code.data);
        } else {
          setCodeBox(null);
        }
      } catch (e) {
        console.warn("Scan frame error:", e);
      } finally {
        busyRef.current = false;
      }
    }
  }, [isScanning, handleResult, decodeQr]);

  // Start scanning interval when scanning is active
  useEffect(() => {
    if (isScanning) {
      scanningIntervalRef.current = setInterval(scanFrame, 100);
    } else if (scanningIntervalRef.current) {
      clearInterval(scanningIntervalRef.current);
      scanningIntervalRef.current = null;
    }

    return () => {
      if (scanningIntervalRef.current) {
        clearInterval(scanningIntervalRef.current);
        scanningIntervalRef.current = null;
      }
    };
  }, [isScanning, scanFrame]);

  // Handle switching camera device
  const handleDeviceChange = async (newDeviceId) => {
    setSelectedDevice(newDeviceId);
    if (isScanning || permissionState === "granted") {
      await startCamera(newDeviceId);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Hidden file input always available in DOM */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
        data-testid="barcode-file-input"
      />

      {/* Switch Mode: Kamera vs Unggah Gambar at top */}
      <div className="mb-4">
        <div className="bg-gray-100 dark:bg-gray-800 p-1.5 rounded-xl flex space-x-1 shadow-inner">
          <button
            type="button"
            onClick={() => {
              setShowUpload(false);
              if (!isScanning && permissionState !== "denied") {
                startCamera();
              }
            }}
            className={`flex-1 flex items-center justify-center space-x-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              !showUpload
                ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm font-semibold"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            <CameraIcon className="h-4 w-4" />
            <span>Kamera</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setShowUpload(true);
              stopCamera();
              if (onError) onError(null);
            }}
            className={`flex-1 flex items-center justify-center space-x-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              showUpload
                ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm font-semibold"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            <Upload className="h-4 w-4" />
            <span>Unggah Gambar</span>
          </button>
        </div>
      </div>

      {/* Upload Section */}
      {showUpload && (
        <div className="mb-4">
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
              isDragOver
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 bg-gray-50 dark:bg-gray-800/50"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            {uploadedImage ? (
              <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="relative inline-block">
                  <img
                    src={URL.createObjectURL(uploadedImage)}
                    alt="Uploaded QR code"
                    className="max-w-full max-h-64 rounded-lg shadow-lg"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearUploadedImage();
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow transition-colors"
                    title="Hapus gambar"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                  {uploadedImage.name}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="mx-auto w-16 h-16 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <ArrowUp className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-lg font-medium text-gray-900 dark:text-white">
                    Unggah Gambar Barcode / QR Code
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Tarik dan lepas gambar di sini, atau klik untuk memilih file
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                    Mendukung JPEG, PNG, GIF, WebP (Maksimal 10MB)
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Camera Live View */}
      {!showUpload && (
        <div className="relative bg-black rounded-xl overflow-hidden shadow-lg aspect-[4/3] sm:aspect-video flex items-center justify-center">
          {/* Video Stream Element */}
          <video
            ref={videoRef}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isCameraReady ? "opacity-100" : "opacity-0"
            }`}
            autoPlay
            playsInline
            muted
          />

          {/* Hidden Canvas for QR parsing */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Camera Loading Spinner & Placeholder */}
          {isCameraLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950 text-white p-6 z-10">
              <RefreshCw className="h-10 w-10 text-blue-500 animate-spin mb-3" />
              <p className="text-base font-medium">Menyiapkan kamera...</p>
              <p className="text-xs text-gray-400 mt-1">
                Mohon tunggu dan izinkan akses jika diminta oleh browser
              </p>
            </div>
          )}

          {/* Camera Permission Denied / Error inside the black box */}
          {!isCameraLoading && (permissionState === "denied" || permissionError) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950/95 text-white p-6 text-center z-10 overflow-y-auto">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center mb-3 shrink-0">
                <ShieldAlert className="h-6 w-6" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
                Izin Akses Kamera Diperlukan
              </h3>
              <p className="text-xs sm:text-sm text-gray-300 max-w-md mb-3 px-2">
                {permissionError ||
                  "Aplikasi membutuhkan izin untuk mengakses kamera agar dapat memindai barcode secara langsung."}
              </p>
              <div className="text-[11px] sm:text-xs text-gray-400 bg-gray-900/80 border border-gray-800 rounded-lg p-2.5 max-w-sm text-left mb-4 space-y-1">
                <p className="font-semibold text-gray-300">Cara mengaktifkan:</p>
                <ol className="list-decimal list-inside space-y-0.5 text-gray-400">
                  <li>Klik ikon gembok / kamera di sebelah kiri URL browser.</li>
                  <li>Ubah izin "Kamera" menjadi "Izinkan" (Allow).</li>
                  <li>Klik tombol "Coba Lagi" di bawah.</li>
                </ol>
              </div>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => startCamera()}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium rounded-lg shadow transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-1.5" />
                  Coba Akses Kamera Lagi
                </button>
              </div>
            </div>
          )}

          {/* Camera Permission Prompt inside the black box */}
          {!isCameraLoading && permissionState === "prompt" && !isScanning && !permissionError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-950/95 text-white p-6 text-center z-10">
              <div className="w-12 h-12 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center mb-3">
                <CameraIcon className="h-6 w-6" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-1">
                Permintaan Izin Kamera
              </h3>
              <p className="text-xs sm:text-sm text-gray-300 max-w-sm mb-4">
                Aplikasi MMS memerlukan akses ke kamera perangkat untuk membaca dan memindai barcode/QR code secara instan.
              </p>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => startCamera()}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium rounded-lg shadow transition-colors"
                >
                  <CameraIcon className="h-4 w-4 mr-1.5" />
                  Izinkan & Mulai Pindai
                </button>
              </div>
            </div>
          )}

          {/* Idle / Camera Off State */}
          {!isScanning && !isCameraLoading && permissionState !== "denied" && permissionState !== "prompt" && !permissionError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/90 text-white p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-3">
                <CameraIcon className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-lg font-semibold">Kamera Tidak Aktif</p>
              <p className="text-sm text-gray-400 max-w-xs mt-1">
                Klik tombol di bawah untuk menyalakan kamera dan mulai memindai barcode.
              </p>
            </div>
          )}

          {/* Scanning Overlay when Active */}
          {isScanning && isCameraReady && (
            <div className="absolute inset-0 pointer-events-none z-10 flex flex-col justify-between p-4">
              {/* Top info badge */}
              <div className="flex justify-center">
                <span className="bg-black/60 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full font-medium flex items-center shadow">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse mr-2" />
                  Mencari barcode / QR code...
                </span>
              </div>

              {/* Focus Box */}
              <div className="relative self-center w-64 h-48 sm:w-72 sm:h-52 border-2 border-white/60 rounded-xl flex items-center justify-center shadow-2xl overflow-hidden">
                {/* Corner highlights */}
                <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-blue-500 rounded-tl-lg pointer-events-none z-10" />
                <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-blue-500 rounded-tr-lg pointer-events-none z-10" />
                <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-blue-500 rounded-bl-lg pointer-events-none z-10" />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-blue-500 rounded-br-lg pointer-events-none z-10" />

                {/* Animated scan laser line moving up and down */}
                <div className="animate-scan-laser" />
              </div>

              {/* Bottom Instructions */}
              <div className="text-center">
                <span className="bg-black/70 backdrop-blur-md text-white text-xs px-4 py-2 rounded-lg font-medium inline-block shadow">
                  Posisikan barcode atau QR code di dalam kotak fokus
                </span>
              </div>
            </div>
          )}

          {/* Detected code outline; viewBox matches the video so "slice" mirrors object-cover */}
          {isScanning && isCameraReady && codeBox && codeBox.points?.length >= 4 && (
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none z-10"
              viewBox={`0 0 ${codeBox.w} ${codeBox.h}`}
              preserveAspectRatio="xMidYMid slice"
              data-testid="code-box"
            >
              <polygon
                points={codeBox.points.map((p) => `${p.x},${p.y}`).join(" ")}
                fill="rgba(34,197,94,0.2)"
                stroke="#22c55e"
                strokeWidth={Math.max(3, codeBox.w / 200)}
                strokeLinejoin="round"
              />
            </svg>
          )}

          {/* Low light hint */}
          {isScanning && isCameraReady && isDark && !flashEnabled && (
            <div className="absolute bottom-4 inset-x-0 flex justify-center z-20">
              <button
                type="button"
                onClick={toggleFlash}
                className="bg-amber-500 text-black text-xs font-semibold px-3 py-1.5 rounded-full shadow"
              >
                Terlalu gelap — nyalakan flash
              </button>
            </div>
          )}

          {/* Camera Controls Overlay (Flashlight) */}
          {isScanning && isCameraReady && (
            <div className="absolute top-4 right-4 flex space-x-2 z-20">
              {/* Flashlight button */}
              <button
                type="button"
                onClick={toggleFlash}
                className="p-2.5 bg-black/60 backdrop-blur-md text-white rounded-full hover:bg-black/80 transition-all shadow"
                title={flashEnabled ? "Matikan flash" : "Nyalakan flash"}
              >
                {flashEnabled ? (
                  <FlashlightOff className="h-5 w-5 text-amber-400" />
                ) : (
                  <Flashlight className="h-5 w-5" />
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Device Selection Dropdown if multiple cameras - placed above Start/Stop Scan button */}
      {devices.length > 1 && !showUpload && (
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
            Pilih Perangkat Kamera
          </label>
          <div className="relative">
            <select
              value={selectedDevice || ""}
              onChange={(e) => handleDeviceChange(e.target.value)}
              className="w-full appearance-none px-4 py-2.5 pl-10 pr-10 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition-colors cursor-pointer"
              title="Pilih Perangkat Kamera"
            >
              {devices.map((device, index) => (
                <option key={device.deviceId || index} value={device.deviceId}>
                  {device.label || `Kamera ${index + 1}`}
                </option>
              ))}
            </select>
            <CameraIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400 pointer-events-none" />
          </div>
        </div>
      )}

      {/* Camera Action Buttons (Start / Stop) with spacing */}
      {!showUpload && (
        <div className="mt-4">
          {!isScanning ? (
            <button
              type="button"
              onClick={() => startCamera()}
              disabled={isCameraLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {isCameraLoading ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span>Membuka Kamera...</span>
                </>
              ) : (
                <>
                  <Scan className="h-5 w-5" />
                  <span>Mulai Pindai</span>
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={stopCamera}
              className="w-full bg-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all shadow-md shadow-red-500/20"
            >
              Hentikan Pemindaian
            </button>
          )}
        </div>
      )}

      {/* Result Display */}
      {lastResult && (
        <div className="mt-5 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
              <Check className="h-5 w-5 text-green-500 mr-2" />
              Hasil Pindai
            </h3>
            <button
              type="button"
              onClick={copyResult}
              className="flex items-center space-x-1 px-3 py-1 bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/60 transition-colors text-xs font-medium"
            >
              {isResultCopied ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  <span>Tersalin!</span>
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  <span>Salin</span>
                </>
              )}
            </button>
          </div>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-100 dark:border-gray-800">
            <p className="text-sm font-mono font-medium text-gray-900 dark:text-white break-all">
              {lastResult}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BarcodeScanner;

